import React, { useEffect, useRef } from 'react';

import style from './index.module.scss';
import { useMeetingTaskDate } from 'util/meeting';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'model';
import Schedule from 'page/TeamworkHome/component/Schedule';
import moment from 'moment';
import { Meeting as MeetingApi } from 'api';
import {
  getCalendarMeetings,
  getUsersMeetingPage,
  getUsersMeetings,
  setMeetingRecord,
  setMeetingRecordsLoading,
  setMeetingRecordsPageInfo
} from 'model/meeting';
import CalendarBar from 'component/CalendarBar';
import { useActivate, useUnactivate } from 'react-activation';
import { useScheduleContext } from 'page/MeetingSchedule/Provider/ScheduleProvider';
import { AllMeetingStatus } from 'type/meeting';
import { SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT } from 'lib/seewoIm';
import { useDisplayConflictRecords } from './useDisplayConflictRecords';

interface IProps {}

const WEEK_TEXT = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const PAGE_SIZE = 20;
const CROSS_DAY_REFRESH_RANDOM_WINDOW = 5 * 60 * 1000;

const getCrossDayRefreshDelay = () => {
  const now = moment();
  return now.clone().add(1, 'day').startOf('day').diff(now) + Math.floor(Math.random() * CROSS_DAY_REFRESH_RANDOM_WINDOW);
};

const ScheduleView: React.FC<IProps> = () => {
  const { originState, scheduleDispatch } = useScheduleContext();
  const { currentTime, isScheduleDateSelected } = originState;
  const calendarMeetingRecords = useSelector((state: RootState) => state.meeting.calendarMeetingRecords);
  const recordsLoading = useSelector((state: RootState) => state.meeting.meetingRecordsLoading);
  const records = useSelector((state: RootState) => state.meeting.meetingRecords);
  const displayRecords = useDisplayConflictRecords(records, isScheduleDateSelected);
  const recordsPageInfo = useSelector((state: RootState) => state.meeting.meetingRecordsPageInfo);
  const { noticeList, taskList } = useMeetingTaskDate(calendarMeetingRecords);
  const dispatch = useDispatch();
  const crossDayRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextRecordsFetchRef = useRef(false);
  const showUpcomingRequestIdRef = useRef(0);

  const onSwitch = (beginTime: number, endTime: number) => {
    dispatch(
      getCalendarMeetings({
        beginTime,
        endTime,
        markConflict: true
      })
    );
  };

  const getUpcomingMeetingParams = (pageNo = 1) => {
    const today = moment().startOf('d');

    return {
      beginTime: today.valueOf(),
      endTime: today.clone().add(30, 'day').valueOf(),
      markConflict: true,
      meetingStatusNotIn: [AllMeetingStatus.FINISH, AllMeetingStatus.FILED],
      pageNo,
      pageSize: PAGE_SIZE
    };
  };

  const fetchUpcomingMeetings = (pageNo = 1, silent = false) => {
    dispatch(
      getUsersMeetingPage({
        ...getUpcomingMeetingParams(pageNo),
        silent
      })
    );
  };

  const fetchMeetings = (selectDate?: number, silent = false) => {
    const selectedDate = moment(selectDate || undefined);
    dispatch(
      getUsersMeetings({
        beginTime: selectedDate.startOf('d').valueOf(),
        endTime: selectedDate.endOf('d').valueOf(),
        markConflict: true,
        silent
      })
    );
  };

  const refreshData = (silent = false, targetTime?: number) => {
    const date = moment(targetTime ?? currentTime ?? undefined);
    const day = date.day();
    dispatch(
      getCalendarMeetings({
        beginTime: date.clone().subtract(day, 'd').startOf('d').valueOf(),
        endTime: date
          .clone()
          .add(6 - day, 'd')
          .endOf('d')
          .valueOf(),
        markConflict: true
      })
    );
    if (isScheduleDateSelected) {
      fetchMeetings(currentTime.valueOf(), silent);
    } else {
      fetchUpcomingMeetings(1, silent);
    }
  };

  const handleCrossDayRefresh = () => {
    if (!isScheduleDateSelected) {
      const today = moment().startOf('d');
      scheduleDispatch({
        type: 'SET_STATE',
        payload: {
          currentTime: today.valueOf()
        }
      });
      refreshData(true, today.valueOf());
      return;
    }

    refreshData(true);
  };

  const clearCrossDayRefreshTimer = () => {
    if (crossDayRefreshTimerRef.current) {
      clearTimeout(crossDayRefreshTimerRef.current);
      crossDayRefreshTimerRef.current = null;
    }
  };

  const scheduleCrossDayRefresh = () => {
    clearCrossDayRefreshTimer();
    crossDayRefreshTimerRef.current = setTimeout(() => {
      handleCrossDayRefresh();
      scheduleCrossDayRefresh();
    }, getCrossDayRefreshDelay());
  };

  const handleShowUpcomingMeetings = () => {
    const requestId = showUpcomingRequestIdRef.current + 1;
    showUpcomingRequestIdRef.current = requestId;
    const today = moment().startOf('d');
    dispatch(setMeetingRecordsLoading(true));
    MeetingApi.getMeetingRecordPage(getUpcomingMeetingParams())
      .toPromise()
      .then((response) => {
        if (requestId !== showUpcomingRequestIdRef.current) return;

        const page = response?.data;
        dispatch(setMeetingRecord(page?.data || []));
        dispatch(
          setMeetingRecordsPageInfo({
            pageNo: page?.pageNo ?? 1,
            pageSize: page?.pageSize ?? PAGE_SIZE,
            total: page?.total ?? 0
          })
        );
        skipNextRecordsFetchRef.current = true;
        scheduleDispatch({
          type: 'SET_STATE',
          payload: {
            currentTime: today.valueOf(),
            isScheduleDateSelected: false
          }
        });
      })
      .catch(() => {})
      .finally(() => {
        if (requestId === showUpcomingRequestIdRef.current) {
          dispatch(setMeetingRecordsLoading(false));
        }
      });
  };

  const onSelectDate = (date: number) => {
    if (isScheduleDateSelected && moment(currentTime).isSame(date, 'day')) {
      handleShowUpcomingMeetings();
      return;
    }

    showUpcomingRequestIdRef.current += 1;
    scheduleDispatch({
      type: 'SET_STATE',
      payload: {
        currentTime: date,
        isScheduleDateSelected: true
      }
    });
  };

  const handleLoadMore = () => {
    if (recordsLoading || records.length >= recordsPageInfo.total) return;

    fetchUpcomingMeetings(recordsPageInfo.pageNo + 1);
  };

  useEffect(() => {
    if (skipNextRecordsFetchRef.current) {
      skipNextRecordsFetchRef.current = false;
      return;
    }

    if (isScheduleDateSelected) {
      fetchMeetings(currentTime.valueOf());
      return;
    }

    fetchUpcomingMeetings();
  }, [currentTime, isScheduleDateSelected]);

  useEffect(() => {
    const handleMeetingScheduleChanged = () => {
      refreshData(true);
    };

    window.addEventListener(SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT, handleMeetingScheduleChanged);
    return () => {
      window.removeEventListener(SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT, handleMeetingScheduleChanged);
    };
  }, [currentTime, isScheduleDateSelected]);

  useEffect(() => {
    scheduleCrossDayRefresh();
    return clearCrossDayRefreshTimer;
  }, [currentTime, isScheduleDateSelected]);

  useActivate(() => {
    scheduleCrossDayRefresh();
    refreshData();
  });

  useUnactivate(() => {
    clearCrossDayRefreshTimer();
  });

  const selectedDate = moment(currentTime);

  return (
    <div className={style.warp}>
      <CalendarBar onSelectDate={onSelectDate} onSwitch={onSwitch} noticeList={noticeList} taskList={taskList} />
      <div className={style.scheduleWarp}>
        {isScheduleDateSelected && (
          <div className={style.selectedDateBar}>
            <div className={style.selectedDateTitle}>{`当日会议・${selectedDate.format('MM月DD日')}（${WEEK_TEXT[selectedDate.day()]}）`}</div>
            <span className={style.upcomingLink} onClick={handleShowUpcomingMeetings}>
              查看待开始会议
            </span>
          </div>
        )}
        <Schedule
          refresh={refreshData}
          records={displayRecords}
          loading={recordsLoading}
          from="schedule"
          mode={isScheduleDateSelected ? 'day' : 'upcoming'}
          showLoadMore={!isScheduleDateSelected}
          hasMore={records.length < recordsPageInfo.total}
          onLoadMore={handleLoadMore}
        />
      </div>
    </div>
  );
};

export default ScheduleView;
